import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { StandingsSidebar, LoadingQuestion, TimerSection, WaitingSpinner } from '../shared';
import { GameEndScreen } from './GameEndScreen';
import { calculateTimeRemaining } from '../../utils/gameUtils';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import { gameUsesTeams } from '../../utils/teamGames';
import type { GameStatistics } from '../../types/game';
import { OneBrainMaze, type OneBrainTeamState } from './oneBrain';
import '../../App.css';
import '../../styles/game-layout.css';
import '../../styles/game-screen.css';
import { useTranslation } from '../../i18n';

interface HostQuestion {
  questionId?: string;
  id?: string;
  question: string;
  title?: string;
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  isOneBrain?: boolean;
  maze?: string[];
  interesting?: string;
  teamStates?: Record<'A' | 'B', OneBrainTeamState | null>;
}

interface OneBrainHostDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{ id: string; name: string; score: number; avatar?: string; teamId?: 'A' | 'B' }>;
    teams?: { A: { id: 'A'; name: string; score: number }; B: { id: 'B'; name: string; score: number } };
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: HostQuestion;
    questionStartTime?: number;
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
}

const OneBrainHostDisplay: React.FC<OneBrainHostDisplayProps> = ({
  roomState,
  socket,
  onBackToGameSelection,
  hostIsPlaying: _hostIsPlaying = false,
  onGoHome,
  onLoadingTimeout,
}) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState<HostQuestion | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState(roomState.currentQuestion?.timeLimit ?? 0);
  const [teamStates, setTeamStates] = useState<Record<'A' | 'B', OneBrainTeamState | null>>({
    A: null,
    B: null,
  });
  const [finishedTeams, setFinishedTeams] = useState<Set<'A' | 'B'>>(new Set());
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<
    Array<{ playerId: string; playerName: string; score: number }>
  >([]);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const gameEndData = useGameEndScreenData(roomState);

  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    } else if (roomState.gameState === 'playing' && gameEnded) {
      setGameEnded(false);
      setCurrentQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      setTeamStates({ A: null, B: null });
      setFinishedTeams(new Set());
      previousQuestionIdRef.current = undefined;
    }
  }, [roomState.gameState, gameEndData, gameEnded]);

  useEffect(() => {
    const q = roomState.currentQuestion;
    if (!q) return;

    const questionId = q.questionId || q.id;
    if (questionId && questionId !== previousQuestionIdRef.current) {
      previousQuestionIdRef.current = questionId;
      setFinishedTeams(new Set());
      setTeamStates({ A: null, B: null });
      // Host race positions are private — fetch after room-state (no longer on CQ).
      socket.emit('get-one-brain-state', {
        roomName: roomState.roomName,
        questionId,
      });
    }

    setCurrentQuestion(q);
    setTimeRemaining(
      calculateTimeRemaining(
        q as Parameters<typeof calculateTimeRemaining>[0],
        roomState.questionStartTime || q.startTime
      )
    );
  }, [roomState.currentQuestion, roomState.questionStartTime, roomState.roomName, socket]);

  useEffect(() => {
    if (!currentQuestion) return;
    const interval = setInterval(() => {
      const start = roomState.questionStartTime || currentQuestion.startTime;
      const remaining = Math.max(0, currentQuestion.timeLimit - (Date.now() - start));
      setTimeRemaining(remaining);
    }, 200);
    return () => clearInterval(interval);
  }, [currentQuestion, roomState.questionStartTime]);

  useEffect(() => {
    const handleQuestion = (data: HostQuestion & { questionId: string; teamStates?: typeof teamStates }) => {
      const qId = data.questionId || data.id;
      if (qId && qId === previousQuestionIdRef.current) return;
      previousQuestionIdRef.current = qId;
      setCurrentQuestion({ ...data, questionId: qId });
      setTimeRemaining(data.timeLimit);
      setFinishedTeams(new Set());
      if (data.teamStates) {
        setTeamStates(data.teamStates);
      } else {
        setTeamStates({ A: null, B: null });
      }
      if (qId && roomState.roomName) {
        socket.emit('get-one-brain-state', { roomName: roomState.roomName, questionId: qId });
      }
    };

    const handleState = (data: {
      questionId: string;
      teamStates: Record<'A' | 'B', OneBrainTeamState | null>;
    }) => {
      const qId = currentQuestion?.questionId || currentQuestion?.id;
      if (data.questionId !== qId) return;
      setTeamStates(data.teamStates);
    };

    const handleFinished = (data: { questionId: string; teamId: 'A' | 'B' }) => {
      const qId = currentQuestion?.questionId || currentQuestion?.id;
      if (data.questionId !== qId) return;
      setFinishedTeams((prev) => new Set(prev).add(data.teamId));
    };

    const handleGameEnded = (data: { leaderboard?: typeof finalLeaderboard; statistics?: GameStatistics }) => {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(data.leaderboard || []);
      setGameStatistics(data.statistics || null);
    };

    const handleGameStarted = () => {
      setGameEnded(false);
      setCurrentQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      setTeamStates({ A: null, B: null });
      setFinishedTeams(new Set());
      previousQuestionIdRef.current = undefined;
    };

    socket.on('question', handleQuestion);
    socket.on('one-brain-state', handleState);
    socket.on('one-brain-team-finished', handleFinished);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);

    return () => {
      socket.off('question', handleQuestion);
      socket.off('one-brain-state', handleState);
      socket.off('one-brain-team-finished', handleFinished);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
    };
  }, [socket, currentQuestion, roomState.roomName]);

  if (gameEnded) {
    const teamLeaderboard = roomState.teams
      ? [
          { playerId: 'A', playerName: roomState.teams.A.name, score: roomState.teams.A.score },
          { playerId: 'B', playerName: roomState.teams.B.name, score: roomState.teams.B.score },
        ].sort((a, b) => b.score - a.score)
      : finalLeaderboard;

    return (
      <GameEndScreen
        finalLeaderboard={teamLeaderboard}
        gameStatistics={gameStatistics}
        onPlaySameGame={() => {
          socket.emit('start-game', { roomName: roomState.roomName, gameType: roomState.gameType });
          setGameEnded(false);
        }}
        onPlayAnotherGame={onBackToGameSelection || (() => {})}
        onBackToGameSelection={onBackToGameSelection}
        roomName={roomState.roomName}
        gameType={roomState.gameType}
      />
    );
  }

  if (!currentQuestion && roomState.gameState === 'playing') {
    return (
      <LoadingQuestion
        socket={socket}
        roomName={roomState.roomName}
        title={t('gameplay.loading.question')}
        onGoHome={onGoHome}
        onLongWaitTimeout={onLoadingTimeout}
      />
    );
  }

  if (!currentQuestion) {
    return (
      <div className="app" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)' }}>
        <Header roomCode={roomState.roomName} />
        <WaitingSpinner message={t('gameplay.oneBrain.waiting')} isLoading />
      </div>
    );
  }

  const maze = currentQuestion.maze ?? [];
  const themeTitle = currentQuestion.title || t('gameplay.oneBrain.hostTitle');
  const timeSeconds = Math.ceil(timeRemaining / 1000);
  const progress = currentQuestion.timeLimit > 0 ? timeRemaining / currentQuestion.timeLimit : 0;

  return (
    <div className="app" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)' }}>
      <Header
        leftButton={
          onBackToGameSelection
            ? { icon: HeaderIcons.back, onClick: onBackToGameSelection, tooltip: t('common.back') }
            : undefined
        }
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.refresh'),
        }}
        roomCode={roomState.roomName}
      />

      <div
        className="game-host-layout"
        style={{ display: 'flex', gap: '1rem', padding: '1rem', height: 'calc(100vh - 80px)' }}
      >
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem', minWidth: 0, alignItems: 'center' }}>
          <TimerSection
            timeSeconds={timeSeconds}
            progress={progress}
            questionNumber={currentQuestion.questionNumber}
            totalQuestions={currentQuestion.totalQuestions}
            primaryColor="var(--color-primary)"
          />

          <h2 style={{ textAlign: 'center', margin: 0, fontSize: '1.5rem' }}>{themeTitle}</h2>

          <p style={{ textAlign: 'center', margin: 0, opacity: 0.75, fontSize: '0.95rem' }}>
            {t('gameplay.oneBrain.hostHint')}
          </p>

          <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            {(['A', 'B'] as const).map((teamId) => (
              <div
                key={teamId}
                style={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: teamId === 'A' ? '#3B82F6' : '#EF4444',
                }}
              >
                {t('gameplay.oneBrain.teamStatus', {
                  team: teamId,
                  status: finishedTeams.has(teamId)
                    ? t('gameplay.oneBrain.statusDone')
                    : t('gameplay.oneBrain.statusRacing'),
                })}
              </div>
            ))}
          </div>

          {maze.length > 0 && <OneBrainMaze maze={maze} teamStates={teamStates} />}
        </div>

        <StandingsSidebar
          players={roomState.players}
          liveAnswers={[]}
          totalPlayers={roomState.players.length}
          questionId={currentQuestion.questionId || currentQuestion.id}
          mode={gameUsesTeams(roomState.gameType) ? 'teams' : 'players'}
          teams={roomState.teams}
          shouldUpdateScores
        />
      </div>
    </div>
  );
};

export default OneBrainHostDisplay;
